import { useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import TemplateDialog, { KIND_USES } from '@/components/admin/TemplateDialog';
import { TEMPLATE_KINDS, TEMPLATE_KIND_STYLES } from '@/shared/constants';
import { TEMPLATE_FIELDS } from '@/shared/fields';
import { rows } from '@/shared/data';

const { eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/templates', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'FileText', label: 'Templates', section: 'Content', profiles: ['Admin_c', 'Staff_c'], order: 75 };

export default function AdminTemplates() {
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('templates_c').select(TEMPLATE_FIELDS);
    if (kind !== 'all') query = query.where(eq('kind_c', kind));
    if (q) query = query.where(contains('Name', q));
    return query.orderBy('Name').limit(100).fetch().then(rows);
  }, [kind, q]);

  const list = data ?? [];
  const filtered = kind !== 'all' || !!q;

  function clear() {
    setSearch('');
    setKind('all');
  }

  const columns = [
    {
      label: 'Template',
      render: (t) => (
        <RowLink to={`/admin/templates/${t.Id}`} sub={t.subject_c}>
          {t.Name}
        </RowLink>
      ),
    },
    { label: 'Used for', render: (t) => <StatusBadge value={t.kind_c} styles={TEMPLATE_KIND_STYLES} /> },
    {
      label: 'Where',
      className: 'hidden md:table-cell text-muted-foreground',
      render: (t) => KIND_USES[t.kind_c] ?? '',
    },
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Content"
        title="Templates"
        description="The emails you copy out of an order: credentials, invoices, expiry and win-back."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            New template
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search templates…" />
        <FilterSelect value={kind} onChange={setKind} values={TEMPLATE_KINDS} allLabel="All uses" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        rows={4}
        isEmpty={!list.length}
        empty={{
          icon: 'FileText',
          title: filtered ? 'No templates match' : 'No templates yet',
          description: filtered ? 'Nothing fits these filters.' : 'Start with the credentials email. It goes out with every new line.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'New template',
        }}
      >
        <DataTable columns={columns} rows={list} />
      </ListBody>

      <TemplateDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
